import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EmailEditorComponent } from 'angular-email-editor';
import { ConfirmationService } from 'primeng/api';
import { Observable } from 'rxjs';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';
import { Template } from 'src/app/models/communication/template';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';

@Component({
  selector: 'app-template-details',
  templateUrl: './template-details.component.html',
  styleUrls: ['./template-details.component.scss']
})
export class TemplateDetailsComponent implements IPendingChangesComponent {
	@ViewChild(EmailEditorComponent) emailEditor: EmailEditorComponent;

	public template: Template;
	public templates: Array<Template> = [];
	public selectedTemplate: Template;
	
	public editorOptions = {};

	private isNew: boolean = false;
	public pageLoaded: boolean = false;
	public errors: any = {};
	public isDirty: boolean = false;

	constructor(private dataService: DataService, private siteService:SiteService, private route: ActivatedRoute, private router: Router, private confirmationService: ConfirmationService) { }

	ngOnInit() {
		this.route.paramMap.subscribe(p => {
			let id = p.get('id');
			if(id == 'new') {
				this.siteService.pageTitle = 'New Template';
				this.template = new Template({ templateID: crypto.randomUUID() });
				this.isNew = true;
			} else {
				this.refreshTemplate(id).then(() => {
					this.siteService.pageTitle = this.template.templateName;
				});
			}
		});
		this.getTemplates();
		this.siteService.headerButtons = [
			{ text: 'Save', class: 'success', callback: () => this.save() },
			{ text: 'Delete', class: 'danger', callback: () => this.delete() }
		];
		this.siteService.backButton = { title: 'Back to Templates', link: '/admin/campaigns/templates' };

		setTimeout(() => this.pageLoaded = true, 1000); //hack to prevent the once function from firing when the page is first loaded

	}
	getTemplates() {
		this.dataService.getTemplates().subscribe((data) => {
			this.templates = data;
		});
	}
	async refreshTemplate(id: string) {
		return new Promise<void>((resolve) => {
			this.dataService.getTemplate(id).subscribe((data) => {
				this.template = data;
				if (!this.template) {
					this.template = new Template({ templateID: id });
					this.template.templateName = 'New Template';
					this.isNew = true;
				}
				resolve();
			});
		});

	}
	editorLoaded(e: any) {
		if(this.template.templateJSON && this.template.templateJSON !== '{}') {
			setTimeout(() =>  this.emailEditor.loadDesign(JSON.parse(this.template.templateJSON)), 1000);
		}
		setTimeout(() => {
			this.emailEditor.editor.registerCallback('design:updated', () => {
				this.isDirty = true;
			});
		}, 1000);

	}
	
	selectTemplate() {
		if(!this.pageLoaded) { return; }
		if(this.template.templateJSON && this.template.templateJSON !== '{}') {
			this.confirmationService.confirm({
				message: "This template is not empty.  By selecting a new template, you will lose any changes you have made to this template.",
				header: "Are you sure?",
				acceptLabel: "Select Template",
				rejectLabel: "Cancel",
				acceptIcon: 'check',
				rejectIcon: 'times',
				rejectButtonStyleClass: 'danger',
				accept: () => {
					if(this.selectedTemplate?.templateJSON && this.selectedTemplate.templateJSON !== '{}') {
						this.emailEditor.loadDesign(JSON.parse(this.selectedTemplate.templateJSON));
					}
				}
			})
		} else {
			if(this.selectedTemplate?.templateJSON && this.selectedTemplate.templateJSON !== '{}') {
				this.emailEditor.loadDesign(JSON.parse(this.selectedTemplate.templateJSON));
			}
		}
	}
	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}
	async save() {
		return new Promise<boolean>(async (resolve, reject) => {
			this.errors = {};
			if(!this.template.templateName) {
				this.errors.templateName = 'Template name is required';
				return;
			}
			await new Promise<void>((resolve2) => {
				this.emailEditor.exportHtml((data: any) => {
					let html = data.html.replace(/&amp;/ig, '&');
					this.template.templateJSON = JSON.stringify(data.design);
					this.template.templateHtml = html; // validated server-side; not persisted
					resolve2();
				});
			});
			this.dataService.saveTemplate(this.template).subscribe(() => {
				this.siteService.setMessage('Template saved');
				this.isNew = false;
				this.refreshTemplate(this.template.templateID);
				this.isDirty = false;
				resolve(true);
			}, (err) => {
				this.errors = err.error.errors;
				reject(false);
			});
		});
	}

	delete() {
		this.confirmationService.confirm({
			message: "Are you sure you want to delete this template?  This action cannot be undone.",
			header: "Delete Template?",
			acceptLabel: "Delete Template",
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			rejectButtonStyleClass: 'danger',
			accept: () => {
				this.dataService.deleteTemplate(this.template.templateID).subscribe(() => {
					this.siteService.setMessage('Template deleted');
					this.router.navigate(['/admin', 'campaigns', 'templates']);
				});
			}
		})
	}
}
